import dayjs from '../../utils/dayjs';
import { BlockStack, Button, Card, DatePicker, InlineGrid, InlineStack, Popover, Text } from '@shopify/polaris';
import { CalendarIcon } from '@shopify/polaris-icons';
import { useCallback, useMemo, useState } from 'react';

import { formatDateRange } from 'components/filter.helper';
import helpers from '../../helpers';

function DateFilterPicker({
  label,
  value,
  onChange,
  disabled,
  disableDatesBefore,
  disableDatesAfter,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  disableDatesBefore?: Date;
  disableDatesAfter?: Date;
}) {
  const initialDate = value ? dayjs(value).toDate() : new Date();
  const [active, setActive] = useState(false);
  const [{ month, year }, setVisibleMonth] = useState({
    month: initialDate.getMonth(),
    year: initialDate.getFullYear(),
  });

  const handleChange = useCallback(
    ({ start }: { start: Date }) => {
      onChange(dayjs(start).format('YYYY-MM-DD'));
      setActive(false);
    },
    [onChange]
  );

  return (
    <BlockStack gap="150">
      <Text as="span" variant="bodyMd">
        {label}
      </Text>
      <Popover
        active={active}
        activator={
          <Button icon={CalendarIcon} disclosure fullWidth textAlign="start" disabled={disabled} onClick={() => setActive((current) => !current)}>
            {value ? dayjs(value).format('DD-MM-YYYY') : 'Chọn ngày'}
          </Button>
        }
        preferredPosition="below"
        autofocusTarget="first-node"
        onClose={() => setActive(false)}
      >
        <Popover.Pane>
          <Popover.Section>
            <DatePicker
              month={month}
              year={year}
              selected={value ? dayjs(value).toDate() : undefined}
              disableDatesBefore={disableDatesBefore}
              disableDatesAfter={disableDatesAfter}
              weekStartsOn={1}
              onMonthChange={(nextMonth, nextYear) => setVisibleMonth({ month: nextMonth, year: nextYear })}
              onChange={handleChange}
            />
          </Popover.Section>
        </Popover.Pane>
      </Popover>
    </BlockStack>
  );
}

export default function ChargeTransactionFilter({ onCallback, loading }: { onCallback: (output: any) => void; loading: boolean }) {
  const mainQuery: any = helpers.ExtractUrl(window.location.search) || {};
  const initialDateRanges = formatDateRange(mainQuery.startedAt);
  const [fromDate, setFromDate] = useState(() => (initialDateRanges[0] ? dayjs(Number(initialDateRanges[0])).format('YYYY-MM-DD') : ''));
  const [toDate, setToDate] = useState(() => (initialDateRanges[1] ? dayjs(Number(initialDateRanges[1])).format('YYYY-MM-DD') : ''));

  const onChangeMainQueryCallback = useMemo(() => helpers.debounce((_value) => onCallback?.call(this, _value), 200), [onCallback]);

  const applyDateFilter = useCallback(
    (nextFromDate: string, nextToDate: string) => {
      const conditions: string[] = [];

      if (nextFromDate) {
        conditions.push(`gte:${dayjs(nextFromDate).startOf('day').valueOf()}`);
      }
      if (nextToDate) {
        conditions.push(`lte:${dayjs(nextToDate).endOf('day').valueOf()}`);
      }

      onChangeMainQueryCallback({ startedAt: conditions.join(','), page: 1 });
    },
    [onChangeMainQueryCallback]
  );

  const handleFromDateChange = useCallback(
    (value: string) => {
      setFromDate(value);
      applyDateFilter(value, toDate);
    },
    [applyDateFilter, toDate]
  );

  const handleToDateChange = useCallback(
    (value: string) => {
      setToDate(value);
      applyDateFilter(fromDate, value);
    },
    [applyDateFilter, fromDate]
  );

  const handleClear = useCallback(() => {
    setFromDate('');
    setToDate('');
    onChangeMainQueryCallback({ startedAt: '', page: 1 });
  }, [onChangeMainQueryCallback]);

  return (
    <Card>
      <BlockStack gap="300">
        <Text as="h2" variant="headingSm">
          Lọc theo thời gian bắt đầu
        </Text>
        <InlineGrid columns={{ xs: 1, sm: 2, md: 2 }} gap="300">
          <DateFilterPicker
            label="Từ ngày"
            value={fromDate}
            onChange={handleFromDateChange}
            disabled={loading}
            disableDatesAfter={toDate ? dayjs(toDate).toDate() : new Date()}
          />
          <DateFilterPicker
            label="Đến ngày"
            value={toDate}
            onChange={handleToDateChange}
            disabled={loading}
            disableDatesBefore={fromDate ? dayjs(fromDate).toDate() : undefined}
            disableDatesAfter={toDate ? dayjs(toDate).toDate() : new Date()}
          />
        </InlineGrid>
        {(fromDate || toDate) && (
          <InlineStack align="end">
            <Button variant="plain" onClick={handleClear} disabled={loading}>
              Xoá bộ lọc ngày
            </Button>
          </InlineStack>
        )}
      </BlockStack>
    </Card>
  );
}

